import { EFinancingProgram, EPaymentPlan } from '@if/core/types'
import { ControlledStringDateOnlyPicker } from '@if/mui'
import { Grid } from '@mui/material'
import InputTextField from '@src/components/InputTextField'
import SelectComponent from '@src/components/SelectComponent'
import { Constants, CreditApplication } from '@src/data/types'
import { ProductsWorksheet, ProductsWorksheetDto } from '@src/data/types/ProductsWorksheetSchema'
import { useEffect, useMemo, useState } from 'react'
import {
  Control,
  FieldErrors,
  UseFormGetValues,
  UseFormRegister,
  UseFormSetValue,
  UseFormWatch,
  useWatch,
} from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useProductsTerms } from '../../editWorksheet-hooks'

interface Props {
  creditApplication: CreditApplication
  worksheet: ProductsWorksheet
  control: Control<ProductsWorksheetDto>
  register: UseFormRegister<ProductsWorksheetDto>
  watch: UseFormWatch<ProductsWorksheetDto>
  setValue: UseFormSetValue<ProductsWorksheetDto>
  getValues: UseFormGetValues<ProductsWorksheetDto>
  errors: FieldErrors<ProductsWorksheetDto>
  merchantPayments: { label: string; value: string }[]
  disabledAmountInput?: boolean
  maxTermDuration: number
  minFirstPaymentDate: Date
  maxFirstPaymentDate: Date
}

const WorksheetDetails = ({
  creditApplication,
  worksheet,
  register,
  errors,
  control,
  watch,
  setValue,
  merchantPayments,
  disabledAmountInput,
  maxTermDuration,
  minFirstPaymentDate,
  maxFirstPaymentDate,
}: Props) => {
  const { t } = useTranslation()
  const [paymentFrequency] = useWatch({ control, name: ['paymentFrequency'] })
  const maxActivationDate = useMemo(() => new Date(creditApplication.expiresOn), [creditApplication.expiresOn])

  const possibleTerms = useProductsTerms(maxTermDuration)

  const [isVariableInterest, setIsVariableInterest] = useState(false)
  const loanTermChoicesList =
    possibleTerms.map((item) => ({
      label: `${item} ${t('worksheet.monthLowerCase')}`,
      value: item,
    })) || []
  const paymentPlanId = watch('paymentPlanId') as EPaymentPlan
  const selectedPlan = useMemo(
    () => merchantPayments?.find((p) => (p.value as EPaymentPlan) === paymentPlanId),
    [merchantPayments, paymentPlanId],
  )

  useEffect(() => {
    const isVariable = Boolean(paymentPlanId && paymentPlanId !== EPaymentPlan.RegularDailyInterests)

    if (isVariable && selectedPlan) {
      setValue('term', Number(selectedPlan.value))
    }

    setIsVariableInterest(isVariable)
  }, [paymentPlanId, merchantPayments, selectedPlan, selectedPlan?.value, setValue])

  const paymentFrequencyOptions = useMemo(
    () =>
      Constants.financingProgramConfigs[EFinancingProgram.Products].supportedPaymentFrequencies.map((item) => ({
        label: t(`EPaymentFrequency.${item}`, { ns: 'enum' }),
        value: item,
      })),
    [t],
  )

  return (
    <Grid container spacing={2} mt={0.5} mb={2}>
      <Grid
        size={{
          xs: 6,
          md: 4,
        }}
      >
        <InputTextField
          label={t('worksheetCommon.amountRequested')}
          fullWidth
          disabled={disabledAmountInput}
          {...register('amountRequested')}
          error={errors.amountRequested}
          defaultValue={worksheet?.amountRequested}
        />
      </Grid>
      <Grid
        size={{
          xs: 6,
          md: 4,
        }}
      >
        <ControlledStringDateOnlyPicker
          name="deliveryOn"
          control={control}
          label={t('worksheetCommon.activationDate')}
          disablePast
          minDate={new Date()}
          maxDate={maxActivationDate}
          fullWidth
        />
      </Grid>
      <Grid
        size={{
          xs: 6,
          md: 4,
        }}
      >
        <SelectComponent
          disabled={isVariableInterest}
          items={loanTermChoicesList}
          label={t('worksheet.term')}
          {...register('term')}
        />
      </Grid>
      <Grid
        size={{
          xs: 6,
          md: 4,
        }}
      >
        <SelectComponent
          items={paymentFrequencyOptions}
          label={t('worksheet.paymentFrequency')}
          {...register('paymentFrequency')}
        />
      </Grid>
      <Grid
        size={{
          xs: 6,
          md: 4,
        }}
      >
        <ControlledStringDateOnlyPicker
          name="firstPaymentOn"
          control={control}
          label={t('worksheet.firstPaymentOn')}
          disablePast
          minDate={minFirstPaymentDate}
          maxDate={maxFirstPaymentDate}
          disabled={!paymentFrequency}
          fullWidth
        />
      </Grid>
      <Grid
        size={{
          xs: 6,
          md: 4,
        }}
      >
        <SelectComponent
          items={merchantPayments}
          label={t('worksheet.paymentPlan')}
          {...register('paymentPlanId')}
          error={errors?.paymentPlanId}
        />
      </Grid>
    </Grid>
  )
}

export default WorksheetDetails
